'use strict';

import axios from 'axios';
import { getAccessToken } from '../utils/Auth.util';
import {
    getPartnerId,
    derivePlanCode,
    hasUMRVisionCoverageWithSpecificNetworkInd,
    getMemberCoverageTypeCodes,
} from '../utils/carePathUtil';
import {
    UMR_VISION_CAREPATHS,
    MICRO_FRONTEND_STARGATE_KEY,
    ENABLE_COVERAGE_TYPES,
} from '../constants/index';

/**
 * calls ui-utils /proxy endpoints to make calls to the HCP carepath endpoints to retrieve shoppable services
 */
class CarePathService {
    /** makes call to proxy for carepath /search endpoint with the user's input search term */
    getCarepathSearchResponse = async (memberDetails, inputTerm, correlationId = null) => {
        const coverageTypeCodes = getMemberCoverageTypeCodes(memberDetails);
        const isCoverageTypesEnabled = ENABLE_COVERAGE_TYPES;
        try {
            const headers = await this.buildRequestHeaders(correlationId);
            const requestBody = isCoverageTypesEnabled
                ? this.buildRequestBody('/search', {
                      term: inputTerm,
                      coverageTypeCodes: coverageTypeCodes,
                  })
                : this.buildRequestBody('/search', {
                      term: inputTerm,
                  });
            const response = await axios.post(process.env.UTILS_PROXY_URL, requestBody, {
                headers: headers,
            });
            let filteredCarepaths = response.data;
            //remove carepthats that are only specific to UMR population with vision coverage as we will get all matching carepaths from API else return all
            if (!hasUMRVisionCoverageWithSpecificNetworkInd(memberDetails)) {
                //remove all carepaths whose carepath id is present in UMR_VISION_CAREPATHS
                filteredCarepaths = filteredCarepaths.filter(
                    (carepath) => !UMR_VISION_CAREPATHS.includes(carepath.carePathId)
                );
            }
            return filteredCarepaths;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `Error in getCarepathSearchResponse Function when calling '${process.env.UTILS_PROXY_URL}' endpoint: ${error}`
            );
            throw error;
        }
    };

    /** makes call to proxy to get full carepath data for selected carepathId */
    getCarepathDataResponse = async (memberDetails, selectedCarePath, correlationId = null) => {
        try {
            const headers = await this.buildRequestHeaders(correlationId);
            const partnerID = getPartnerId(memberDetails);
            const planCode = derivePlanCode(memberDetails, selectedCarePath.carePathCategoryCode);
            const payload = {
                policyId: memberDetails?.policyNumber,
                partnerId: partnerID,
                ...(planCode && {
                    planCode: planCode, //TODO: Figure out how to retrieve planCode for all populations
                }),
            };

            const requestBody = this.buildRequestBody(
                `/carepaths/${selectedCarePath.carePathId}`,
                payload
            );

            const response = await axios.post(process.env.UTILS_PROXY_URL, requestBody, {
                headers: headers,
            });

            return response.data;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `Error in getCarepathDataResponse Function when calling '${process.env.UTILS_PROXY_URL}' endpoint: ${error}`
            );
            throw error;
        }
    };

    buildRequestHeaders = async (correlationId) => {
        const HCP_Stargate_token = await getAccessToken(
            process.env.ENV == 'uat' ? 'HCP_NonProd_Stargate_token' : 'HCP_Stargate_token',
            process.env.ACCESS_TOKENS_URL_NONPROD
        );
        const token = await getAccessToken(MICRO_FRONTEND_STARGATE_KEY);
        return {
            'Content-Type': 'application/json',
            'X-Upstream-Env':
                process.env.ENV === 'prod'
                    ? 'prod'
                    : process.env.ENV === 'uat'
                    ? 'uat'
                    : 'integration',
            Authorization: 'Bearer ' + token,
            'x-downstream-authorization': HCP_Stargate_token,
            ...(correlationId && {
                'x-epe-correlationid': correlationId,
            }),
        };
    };

    buildRequestBody = (path, payload) => {
        return {
            method: 'POST',
            targetUrl: 'CAREPATH_URL',
            path: path,
            payload: payload,
        };
    };

    /**
     * makes call to carepath endpoint to get carepaths data for selected revenueCode
     * @param {*} revenueCodeId - Revenue code id for which we have to get cpt codes
     * @param {*} correlationId - unique id to differentiate between requests and track logs
     * @returns an array of cpt codes that are part of revenueCodeId.
     */
    getCarepathDataForRevenueCode = async (revenueCodeId, correlationId = null) => {
        try {
            const headers = await this.buildRequestHeaders(correlationId);
            const requestBody = this.buildRequestBody(`/groupedCarePaths/${revenueCodeId}`, {});

            const response = await axios.post(process.env.UTILS_PROXY_URL, requestBody, {
                headers: headers,
            });

            return response.data;
        } catch (error) {
            console.error(
                `Error in getCarepathDataForRevenueCode Function when calling 'groupedCarePaths' endpoint: ${error}`
            );
            return [];
        }
    };
}

export default new CarePathService();
